import { planningContractType } from "../../utils/planningContractType";
import { mouradPlanningContracts } from "../../utils/mouradPlanningContracts";
import React, { useEffect, useState } from "react";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";
import {
  contractDate,
  contractTime,
  missionOnDay,
  monday,
} from "../../utils/planningDates";

const iso = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const frenchDate = (value) =>
  contractDate(value)?.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) || "—";
const weekdays = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];
const periods = ["matin", "après-midi", "soir", "nuit"];
const input = "rounded-lg border border-slate-200 px-3 py-2 text-sm";
export default function AvailableTable({
  candidate,
  setCandidates,
  onUpdated,
}) {
  const [week, setWeek] = useState(() => new Date());
  const [view, setView] = useState("week");
  const [contracts, setContracts] = useState(() =>
    mouradPlanningContracts(candidate),
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [availabilities] = useState(candidate.availabilities || []);
  const [unavailabilities, setUnavailabilities] = useState(
    candidate.unavailabilities || [],
  );
  const [form, setForm] = useState({
    start: "",
    end: "",
    period: "journée",
    reason: "",
  });
  useEffect(() => {
    let active = true;
    if (candidate.pld == null || String(candidate.pld).trim() === "") {
      setLoading(false);
      return;
    }
    axios
      .get(`pld-contract/candidate/${candidate.pld}`)
      .then(({ data }) => {
        if (active)
          setContracts([
            ...mouradPlanningContracts(candidate),
            ...(Array.isArray(data) ? data : []),
          ]);
      })
      .catch(() => {
        if (active) setError("Impossible de charger les missions.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [candidate]);
  const monthStart = new Date(week.getFullYear(), week.getMonth(), 1);
  const gridStart = monday(view === "month" ? monthStart : week);
  const monthEnd = new Date(week.getFullYear(), week.getMonth() + 1, 0);
  const lastWeek = monday(monthEnd);
  const numberOfDays =
    view === "month" ? Math.round((lastWeek - gridStart) / 86400000) + 7 : 7;
  const days = Array.from({ length: numberOfDays }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(date.getDate() + index);
    return date;
  });
  const calendarWeeks = Array.from({ length: numberOfDays / 7 }, (_, index) =>
    days.slice(index * 7, index * 7 + 7),
  );
  const periodStart = iso(view === "month" ? monthStart : days[0]);
  const periodEnd = iso(view === "month" ? monthEnd : days[6]);
  const visibleUnavailabilities = unavailabilities
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.start <= periodEnd && row.end >= periodStart);
  const shift = (direction) => {
    setWeek(
      view === "month"
        ? new Date(week.getFullYear(), week.getMonth() + direction, 1)
        : new Date(
            week.getFullYear(),
            week.getMonth(),
            week.getDate() + direction * 7,
          ),
    );
  };
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const payload = { availabilities, unavailabilities };
      const { data } = await axios.post(
        `tempworker-candidate/${candidate._id}/update`,
        payload,
      );
      const updated = { ...candidate, ...payload, ...(data?._id ? data : {}) };
      onUpdated?.(updated);
      setCandidates?.((rows) =>
        rows.map((row) => (row._id === candidate._id ? updated : row)),
      );
      toast.success("Disponibilités enregistrées.");
    } catch {
      toast.error("Impossible d’enregistrer les disponibilités.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Disponibilités</h2>
        <p className="mt-1 text-sm text-slate-500">
          Planning du candidat, missions et périodes d’indisponibilité.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div
          className="flex rounded-lg bg-slate-100 p-1"
          role="group"
          aria-label="Vue du planning"
        >
          {[
            { id: "week", label: "Semaine" },
            { id: "month", label: "Mois" },
          ].map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={view === option.id}
              onClick={() => setView(option.id)}
              className={`rounded-md px-4 py-2 text-sm font-semibold ${view === option.id ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          className={input}
          onClick={() => shift(-1)}
          aria-label={
            view === "month" ? "Mois précédent" : "Semaine précédente"
          }
        >
          ←
        </button>
        <button className={input} onClick={() => setWeek(new Date())}>
          Aujourd’hui
        </button>
        <button
          className={input}
          onClick={() => shift(1)}
          aria-label={view === "month" ? "Mois suivant" : "Semaine suivante"}
        >
          →
        </button>
        <input
          aria-label={
            view === "month" ? "Choisir le mois" : "Choisir la semaine"
          }
          type={view === "month" ? "month" : "date"}
          className={input}
          value={view === "month" ? iso(week).slice(0, 7) : iso(week)}
          onChange={(e) => {
            const date = contractDate(
              view === "month" ? `${e.target.value}-01` : e.target.value,
            );
            if (date) setWeek(date);
          }}
        />
        <span className="text-sm text-slate-500">
          {view === "month"
            ? week.toLocaleDateString("fr-FR", {
                month: "long",
                year: "numeric",
              })
            : `Du ${days[0].toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })} au ${days[6].toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}`}
        </span>
      </div>
      {loading && <p role="status">Chargement des missions…</p>}
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table data-pagination="none" className="w-full min-w-[800px] table-fixed text-sm">
          <thead>
            <tr>
              {days.slice(0, 7).map((day, index) => (
                <th
                  key={iso(day)}
                  className="border-r bg-slate-50 p-3 text-left"
                >
                  {weekdays[index]}
                  {view === "week" && (
                    <p className="text-xs font-normal text-slate-500">
                      {day.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}
                    </p>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calendarWeeks.map((weekDays) => (
              <tr key={iso(weekDays[0])}>
                {weekDays.map((day) => {
                  const missions = contracts.filter((row) =>
                    missionOnDay(row, day),
                  );
                  const absent = unavailabilities.filter(
                    (row) =>
                      row.start <= iso(day) &&
                      row.end >= iso(day) &&
                      (view !== "month" || day.getMonth() === week.getMonth()),
                  );
                  const available = availabilities.filter((value) =>
                    value.startsWith(`${weekdays[(day.getDay() + 6) % 7]} : `),
                  );
                  return (
                    <td
                      key={iso(day)}
                      data-date={iso(day)}
                      className={`h-48 border-b border-r p-2 align-top ${day.toDateString() === new Date().toDateString() ? "bg-blue-50/50" : view === "month" && day.getMonth() !== week.getMonth() ? "bg-slate-50 text-slate-400" : "bg-white"}`}
                    >
                      {view === "month" && (
                        <p
                          className={`mb-2 text-xs font-semibold ${day.toDateString() === new Date().toDateString() ? "text-blue-700" : "text-slate-500"}`}
                        >
                          {day.toLocaleDateString("fr-FR", { ...(day.getMonth() !== week.getMonth()
                              ? { month: "short" }
                              : {}), day: "2-digit", month: "2-digit", year: "numeric" })}
                        </p>
                      )}
                      <div className="space-y-2">
                        {missions.map((row, index) => (
                          <div
                            key={row._id || index}
                            className={`rounded-lg border p-2 text-xs ${planningContractType(row).className}`}
                          >
                            <strong>{row.RaisonSociale || "Mission"}</strong>
                            <p className="mt-1 font-semibold">{planningContractType(row).label}</p>
                            {row.isDemo && (
                              <p className="font-semibold">
                                Contrat de test · {row.NumeroContrat}
                              </p>
                            )}
                            <p>
                              {[
                                contractTime(row.HeureDebut),
                                contractTime(row.HeureFin),
                              ]
                                .filter(Boolean)
                                .join(" → ") || "Toute la journée"}
                            </p>
                          </div>
                        ))}
                        {absent.map((row, index) => (
                          <div
                            key={row.id || index}
                            className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-900"
                          >
                            <strong>
                              Indisponible · {row.period || "journée"}
                            </strong>
                            <p>{row.reason}</p>
                            <p>
                              {row.source === "candidate"
                                ? "Déclaré par le candidat"
                                : "Déclaré par l’agence"}
                            </p>
                          </div>
                        ))}
                        {available
                          .filter(
                            (value) =>
                              !absent.some(
                                (row) =>
                                  !row.period ||
                                  row.period === "journée" ||
                                  row.period === value.split(" : ")[1],
                              ),
                          )
                          .map((value) => (
                            <p
                              key={value}
                              className="rounded-lg bg-blue-50 p-2 text-xs text-blue-700"
                            >
                              Disponible · {value.split(" : ")[1]}
                            </p>
                          ))}
                        {!missions.length &&
                          !absent.length &&
                          !available.length && (
                            <p className="py-8 text-center text-xs text-slate-400">
                              Non renseigné
                            </p>
                          )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <fieldset disabled={busy} className="space-y-4">
        <h3 className="text-lg font-semibold">Ajouter une indisponibilité</h3>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!form.start || !form.end || form.end < form.start) return;
            setUnavailabilities((rows) => [
              ...rows,
              { ...form, id: crypto.randomUUID(), source: "agency" },
            ]);
            setForm({ start: "", end: "", period: "journée", reason: "" });
          }}
        >
          <label className="text-sm">
            Du
            <input
              required
              type="date"
              className={`${input} block`}
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Au
            <input
              required
              min={form.start}
              type="date"
              className={`${input} block`}
              value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Période
            <select
              className={`${input} block`}
              value={form.period}
              onChange={(e) => setForm({ ...form, period: e.target.value })}
            >
              {["journée", ...periods].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Motif
            <input
              className={`${input} block`}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>
          <button className="rounded-lg bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
            Ajouter au planning
          </button>
        </form>
        {visibleUnavailabilities.map(({ row, index }) => (
          <div
            key={row.id || index}
            className="flex items-center justify-between gap-3 rounded-lg bg-rose-50 p-3 text-sm"
          >
            <span>
              {frenchDate(row.start)} → {frenchDate(row.end)} ·{" "}
              {row.period || "journée"} · {row.reason}
            </span>
            <button
              onClick={() =>
                setUnavailabilities((rows) =>
                  rows.filter((_, i) => i !== index),
                )
              }
              aria-label={`Supprimer l’indisponibilité du ${frenchDate(row.start)}`}
              className="text-rose-700"
            >
              Supprimer
            </button>
          </div>
        ))}

        <div className="flex justify-end">
          <button
            disabled={busy}
            onClick={save}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white"
          >
            {busy ? "Enregistrement…" : "Enregistrer les modifications"}
          </button>
        </div>
      </fieldset>
    </div>
  );
}
